"use client";

import { Button } from "@ui/shadcn/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@ui/shadcn/command";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/shadcn/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import useSWR from "swr";
import { getProductsAction } from "@/actions/admin";
import { cn } from "@/lib/utils";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import type { ProductDetails } from "./product-details";

export function ProductRelatedSelect({
	selectedProduct,
	position,
}: {
	selectedProduct: string;
	position: number;
}) {
	const { data: result, isLoading } = useSWR("getProductsAction", () => getProductsAction(), {
		keepPreviousData: true,
	});

	const products = result?.data || [];
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState(selectedProduct);

	const context = useStoreBuilderContext<typeof ProductDetails>();

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className="flex w-full justify-between"
				>
					{isLoading ? (
						<div className="text-muted-foreground">loading ... </div>
					) : (
						<span>
							{value ? products.find((product) => product.id === value)?.name : "Select product..."}
						</span>
					)}
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[280px] p-0">
				<Command>
					<CommandInput placeholder="Search product..." className="focus-visible:[box-shadow:none]" />
					<CommandList>
						<CommandEmpty>No product found.</CommandEmpty>
						<CommandGroup>
							<CommandItem
								onSelect={async (_currentValue) => {
									setValue("");

									await context.saveSelectedPageSectionField({
										fieldPath: `relatedProducts.${position}`,
										value: null,
									});

									setOpen(false);
								}}
							>
								None
							</CommandItem>
							{products.map((product) => (
								<CommandItem
									key={product.id}
									value={product.id}
									onSelect={async (currentValue) => {
										setValue(currentValue);

										await context.saveSelectedPageSectionField({
											fieldPath: `relatedProducts.${position}`,
											value: currentValue,
										});

										setOpen(false);
									}}
								>
									<Check className={cn("mr-2 h-4 w-4", value === product.id ? "opacity-100" : "opacity-0")} />
									{product.name}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
